import React, { useState } from 'react';
import { Select, Typography } from 'antd';
import { MACHINERY_MANUFACTURER_BRANDS } from '../constants/machineryManufacturerBrands';

const { Text } = Typography;

const BRAND_OPTIONS = MACHINERY_MANUFACTURER_BRANDS.map((brand) => ({ value: brand, label: brand }));

interface ManufacturerBrandSelectProps {
  label: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  disabled?: boolean;
}

/**
 * Manufacturer picker for Machinery's own ManufacturerBrand field -- a real
 * single-value dropdown, not a bare text box. Before this, the field used
 * FloatingLabelAutoComplete with per-category suggestions from
 * machineryBrandsSeed.ts, seeded for only the 4 categories that predate the
 * 83-type taxonomy -- every other category got zero suggestions, so on
 * mobile in particular it read as "just an input".
 *
 * A plain single-select `Select` (not `mode="tags"`, which let the host
 * pick more than one brand -- ManufacturerBrand is a single string, and
 * antd's own tags-mode chips visually allowed adding several before this
 * component's own onChange truncated it back down, which is what read as
 * "lets me pick more than one manufacturer"). Still free text underneath
 * (see FloatingLabelAutoComplete's own doc comment: an unlisted host's rig
 * must never be blocked from publishing) via a synthetic option injected at
 * the top of the list whenever the typed search text doesn't match any
 * curated brand -- selecting it sets the value to exactly what was typed,
 * same as picking any other option.
 */
const ManufacturerBrandSelect: React.FC<ManufacturerBrandSelectProps> = ({ label, value, onChange, disabled }) => {
  const [searchValue, setSearchValue] = useState('');
  const trimmedSearch = searchValue.trim();
  const matchesExistingBrand = BRAND_OPTIONS.some((option) => option.label.toLowerCase() === trimmedSearch.toLowerCase());
  const options =
    trimmedSearch && !matchesExistingBrand ? [{ value: trimmedSearch, label: trimmedSearch }, ...BRAND_OPTIONS] : BRAND_OPTIONS;

  return (
    <div>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
        {label}
      </Text>
      <Select
        showSearch
        allowClear
        disabled={disabled}
        value={value}
        searchValue={searchValue}
        onSearch={setSearchValue}
        onChange={(next) => {
          onChange?.(next ?? undefined);
          setSearchValue('');
        }}
        options={options}
        style={{ width: '100%' }}
        filterOption={(input, option) => !!option?.label && String(option.label).toLowerCase().includes(input.toLowerCase())}
      />
    </div>
  );
};

export default ManufacturerBrandSelect;
